import React, {useEffect, useRef, useState} from 'react';
import {useAppContext} from 'contexts/ApplicationContext';
import {useJobContext} from 'contexts/JobContext';
import {Link, useSearchParams} from 'react-router-dom';
import {PartialJob} from 'shared/types/PartialJob';
import {useFetchStripeStatusQuery} from 'components/customer/Confirmation/store/gatewayApi';
import {CBCButton, Icon, Loader} from 'shared/helpers';
import styled from 'styled-components';
import {mobile} from 'shared/Breakpoints';
import {useSubmitJob} from 'components/customer/Job/helpers/useSubmitJob';
import {StripePaymentStatus} from 'components/customer/Confirmation/Gateways/Stripe/entity/StripePaymentStatus';
import {Stack} from 'react-bootstrap';
import {Container} from 'components/customer/Confirmation/JobConfirmation';
import {useNativeRedirect} from 'shared/helpers/useNativeRedirect';
import {useJobDialog} from 'components/customer/Job/helpers/useJobDialog';

const POLL_LIMIT = 3;
const POLL_INTERVAL = 5000;

export const PaymentConfirmation = () => {
    const {userProfile} = useAppContext();
    const [searchParams] = useSearchParams();
    const {job} = useJobContext() as PartialJob;
    const [poll, setPoll] = useState(false); // poll if status is open
    const {data, isError, isFetching, isLoading, isUninitialized} =
        useFetchStripeStatusQuery(
            {
                intentId: searchParams.get('payment_intent'),
                manufacturerId: userProfile.manufacturerId,
                jobId: job.id,
            },
            {
                skip:
                    !job?.id ||
                    !userProfile?.manufacturerId ||
                    !searchParams.has('payment_intent'),
                pollingInterval: poll ? POLL_INTERVAL : undefined,
                skipPollingIfUnfocused: true,
            }
        );

    const submitJob = useSubmitJob(true);
    const {redirect} = useNativeRedirect();
    const {openJobDialog, jobDialog} = useJobDialog();

    const pollCounter = useRef(0);
    useEffect(() => {
        if (isFetching == false) {
            if (pollCounter.current <= POLL_LIMIT) {
                if (data && data.status == StripePaymentStatus.OPEN) {
                    setPoll(true);
                    pollCounter.current += 1;
                }
            } else {
                setPoll(false);
            }
        }
    }, [data, isFetching]);

    const jobSubmittedRef = useRef(false);
    useEffect(() => {
        if (data && data.status == StripePaymentStatus.COMPLETE) {
            if (searchParams.has('balance-payment')) {
                // no need to submit job here
                return;
            }

            if (
                typeof submitJob.action == 'function' &&
                !jobSubmittedRef.current
            ) {
                jobSubmittedRef.current = true;
                void submitJob.action(undefined);
            }
        }
    }, [data?.status, submitJob, searchParams]);

    if (isError) {
        return (
            <Container>
                <Icon iconName="product_not_found.svg" />
                <h1>Error!</h1>
                <div>
                    There was error getting payment status, Please try again
                    later.
                </div>
            </Container>
        );
    }

    if (isUninitialized || isLoading) {
        return (
            <Container>
                <Loader loader />
            </Container>
        );
    }

    if (data.status == StripePaymentStatus.REQUIRES_ACTION) {
        // NOTE: this only applies to QR Code and micro transactions
    }

    if (data.status == StripePaymentStatus.COMPLETE) {
        return (
            <Container>
                <Icon iconName="Button-Tick-green.svg" />
                <h1>Payment confirmed</h1>
                <div>
                    We’ve received your payment. A receipt has been sent to your
                    email.
                </div>
                <Stack
                    gap={2}
                    className="flex-column flex-md-row justify-content-center mt-2">
                    <CBCButton
                        type="button"
                        iconName="Home-White.svg"
                        className="job-button button-blue full-width-xs"
                        onClick={redirect('/v2/')}>
                        Return home
                    </CBCButton>

                    <CBCButton
                        type="button"
                        iconName="Button-Add.svg"
                        className="job-button button-primary full-width-xs"
                        onClick={openJobDialog}>
                        Start new job
                    </CBCButton>
                    {jobDialog}
                </Stack>
            </Container>
        );
    }

    if (
        data.status == StripePaymentStatus.EXPIRED ||
        data.status == StripePaymentStatus.CANCELED ||
        (data.status == StripePaymentStatus.OPEN &&
            pollCounter.current == POLL_LIMIT + 1)
    ) {
        return (
            <Container>
                <Icon iconName="product_not_found.svg" />
                <h1>Payment was not successful</h1>
                <div>
                    We&apos;re sorry - Looks like your payment didn&apos;t go
                    through. Please try again or use a different payment method.
                </div>
                <Link to={`/v2/job/${job.displayId}/payment`}>
                    Return to payment
                </Link>
                <hr />
                <Help>
                    <div>
                        <strong>Need help?</strong>
                    </div>
                    <div>
                        <div>
                            <Icon iconName="email.svg" />
                            <a
                                href={`mailto:${userProfile?.manufacturerEmail}`}>
                                {userProfile?.manufacturerEmail}
                            </a>
                        </div>
                        <div>
                            <Icon iconName="phone.svg" />
                            <a href={`tel:${userProfile?.manufacturerPhone}`}>
                                {userProfile?.manufacturerPhone}
                            </a>
                        </div>
                    </div>
                </Help>
            </Container>
        );
    }

    return (
        <Container>
            <Icon iconName="Gear.svg" />
            <h1>Checking payment status</h1>
            <div>Please wait while we check the payment status</div>
        </Container>
    );
};

export const Help = styled.div<{$centerAlign?: boolean}>`
    display: flex;
    gap: 15px;
    justify-content: space-between;
    align-items: flex-start;

    ${({$centerAlign = false}) => {
        if ($centerAlign) {
            return `
                flex-direction: column;
                align-items: center;

                > div:last-child {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }
            `;
        }
    }}

    > div:first-child {
        text-align: left;
    }

    > div:last-child {
        > div {
            display: flex;
            gap: 5px;
            align-items: center;
        }

        img {
            width: 20px;
        }
    }

    ${mobile`
        flex-direction: column;
        align-items: center;

        > div:last-child {
            display: flex;
            flex-direction: column;
            align-items: center;
        }
    `}
`;
